import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { enumRespErrors } from '@data-layer/enums/common';
import { HTS_ASSISTANT_ANSWER_MAX_LENGTH } from '../const/hts-assistant.const';
import { IHtsAssistantNotice } from '../interfaces';

export interface IHtsAssistantErrorOutcome {
  notice: IHtsAssistantNotice;
  /** The refusal says the conversation moved on (completed, ended, no answer): re-read it instead of guessing. */
  isRefreshActive: boolean;
  /** `entity_is_locked`: the entry can no longer be edited — the drawer turns read-only. */
  isLocked: boolean;
}

/** Backend refusal tags of the classification assistant → inline notices (#13383). Pure: the facade applies the side effects. */
export class HtsAssistantErrorHelper {
  public static isTag(err: HttpErrorResponse, tag: enumRespErrors): boolean {
    return !!err && !!err.error && err.error.tag === tag;
  }

  public static hasTag(err: HttpErrorResponse): boolean {
    return !!err && !!err.error && !!err.error.tag;
  }

  public static map(err: HttpErrorResponse): IHtsAssistantErrorOutcome {
    const tag: string | null = err && err.error ? err.error.tag : null;
    // the rate limit (20 classifier calls a minute per user) answers 429 before anything else runs
    if ((err && err.status === HttpStatusCode.TooManyRequests) || tag === enumRespErrors.HtsChatTooManyRequests) {
      return HtsAssistantErrorHelper.info('HTS_ASSISTANT.NOTICE.TOO_MANY_REQUESTS');
    }

    switch (tag) {
      case enumRespErrors.HtsChatClassifierTimeout:
      case enumRespErrors.HtsChatClassifierUnavailable:
      case enumRespErrors.HtsChatLinkConflict:
        return HtsAssistantErrorHelper.error('HTS_ASSISTANT.NOTICE.TRY_AGAIN');
      case enumRespErrors.HtsChatNoAnswer:
        // the conversation finished with "no answer" meanwhile: show its state instead of an error
        return HtsAssistantErrorHelper.info('HTS_ASSISTANT.NOTICE.NO_ANSWER', true);
      case enumRespErrors.HtsChatResultUnknownCode:
      case enumRespErrors.HtsChatResultExpiredCode:
      case enumRespErrors.CatalogItemCodeWithdrawn:
      case enumRespErrors.HtsSuggestionCodeWithdrawn:
        return HtsAssistantErrorHelper.error('HTS_ASSISTANT.NOTICE.CODE_UNAVAILABLE');
      case enumRespErrors.HtsSuggestionNotFound:
      case enumRespErrors.HtsSuggestionNotSimilar:
        return HtsAssistantErrorHelper.info('HTS_ASSISTANT.NOTICE.SUGGESTION_STALE');
      case enumRespErrors.HtsChatClassifierUnauthorized:
      case enumRespErrors.HtsChatClassifierRejectedRequest:
      case enumRespErrors.HtsChatClassifierUnreadable:
      case enumRespErrors.HtsChatDescriptionTooLong:
        return HtsAssistantErrorHelper.error('HTS_ASSISTANT.NOTICE.OUR_SIDE');
      case enumRespErrors.HtsChatAnswerRequired:
      case enumRespErrors.HtsChatMessageRequired:
      case enumRespErrors.HtsChatMessagesRequired:
        return HtsAssistantErrorHelper.error('HTS_ASSISTANT.NOTICE.ANSWER_REQUIRED');
      case enumRespErrors.HtsChatAnswerTooLong:
      case enumRespErrors.HtsChatMessageTooLong:
        return HtsAssistantErrorHelper.error('HTS_ASSISTANT.NOTICE.ANSWER_TOO_LONG', { max: HTS_ASSISTANT_ANSWER_MAX_LENGTH });
      case enumRespErrors.HtsChatTurnInProgress:
        return HtsAssistantErrorHelper.info('HTS_ASSISTANT.NOTICE.TURN_IN_PROGRESS');
      case enumRespErrors.HtsChatCompleted:
        return HtsAssistantErrorHelper.info('HTS_ASSISTANT.NOTICE.COMPLETED', true);
      case enumRespErrors.HtsChatEnded:
        return HtsAssistantErrorHelper.info('HTS_ASSISTANT.NOTICE.ENDED', true);
      case enumRespErrors.HtsChatNotFinished:
        return HtsAssistantErrorHelper.error('HTS_ASSISTANT.NOTICE.NOT_FINISHED');
      case enumRespErrors.HtsChatAlreadyLinked:
        return HtsAssistantErrorHelper.error('HTS_ASSISTANT.NOTICE.ALREADY_LINKED');
      case enumRespErrors.HtsChatEntrySummaryOnly:
        return HtsAssistantErrorHelper.error('HTS_ASSISTANT.NOTICE.ENTRY_SUMMARY_ONLY');
      case enumRespErrors.EntityIsLocked:
        return { notice: { textKey: 'HTS_ASSISTANT.NOTICE.LOCKED', isError: false }, isRefreshActive: false, isLocked: true };
      case enumRespErrors.BetaFeatureDisabled:
        return HtsAssistantErrorHelper.error('HTS_ASSISTANT.NOTICE.FEATURE_DISABLED');
      default:
        return HtsAssistantErrorHelper.error('HTS_ASSISTANT.NOTICE.TRY_AGAIN');
    }
  }

  private static error(textKey: string, params?: { [key: string]: string | number }): IHtsAssistantErrorOutcome {
    return { notice: { textKey, params, isError: true }, isRefreshActive: false, isLocked: false };
  }

  private static info(textKey: string, isRefreshActive: boolean = false): IHtsAssistantErrorOutcome {
    return { notice: { textKey, isError: false }, isRefreshActive, isLocked: false };
  }
}
